import { calcPlanDailyTargets, isTemplateFixedHighCarbDay } from '@/data/diet-plan-templates';
import type { IDietPlan, IUserProfile } from '@/hooks/useAppState';
import type { TrainingDayType } from '@/data/training-plan-generator';
import { dietDayIndex, findDietSnapshot } from '@/lib/dietService';
import { isAttachedFatLoss } from '@/data/attached-fat-loss';
import { weeklyHoursAtDate } from './trainingScheduleHistory';

type CarbDay = 'high' | 'medium' | 'low';
export interface DietDateContext {
  plan: IDietPlan | null;
  profile: Pick<IUserProfile, 'trainingFrequency' | 'strengthDays' | 'goal' | 'age'>;
  memberDay?: { type: TrainingDayType } | null;
  memberCarbDay?: CarbDay;
  plans?: IDietPlan[];
}

// Match useTodayDiet's existing ordinary-user schedule; nutrition still uses its public calculator.
function defaultStrengthDays(frequency: string) {
  if (frequency === '1-2') return [1, 5];
  if (frequency === '5-6') return [1, 2, 4, 5, 6];
  return [1, 3, 5];
}

function cardioDays(strength: number[], count: number) {
  const result: number[] = [];
  for (const day of strength) {
    const next = day === 7 ? 1 : day + 1;
    if (!strength.includes(next) && !result.includes(next)) result.push(next);
    if (result.length >= count) return result;
  }
  for (const day of [7, 6, 3]) {
    if (!strength.includes(day) && !result.includes(day)) result.push(day);
    if (result.length >= count) return result;
  }
  return result;
}

export function resolveDietDate(date: Date, { plan, profile, memberDay, memberCarbDay, plans = [] }: DietDateContext) {
  const snapshot = findDietSnapshot(plans, date.toDateString());
  if (snapshot) return { ...snapshot, snapshot };
  // Keep the elapsed-time convention used by useTodayDiet for legacy startDate timestamps.
  const dayIndex = plan?.startDate ? dietDayIndex(plan, date) : 1;
  const strength = profile.strengthDays?.length
    ? profile.strengthDays : defaultStrengthDays(profile.trainingFrequency || '3-4');
  const day = date.getDay() || 7;
  const goal = plan?.goal || profile.goal || 'fat-loss';
  const regularCarbDay: CarbDay = strength.includes(day) ? 'high'
    : cardioDays(strength, goal === 'muscle-gain' ? 2 : 3).includes(day) ? 'medium' : 'low';
  const scheduleCarbDay = memberDay && memberCarbDay ? memberCarbDay : regularCarbDay;
  const isTrainingDay = memberDay ? ['strength', 'cardio', 'hiit'].includes(memberDay.type) : regularCarbDay === 'high';
  const fixedHigh = isTemplateFixedHighCarbDay(plan?.planTemplate || 'generic', dayIndex);
  const carbDay: CarbDay = fixedHigh ? 'high' : scheduleCarbDay;
  const carbDaySubLabel = fixedHigh ? '高碳冲击日' : memberDay
    ? memberDay.type === 'strength' ? '训练日'
      : memberDay.type === 'cardio' || memberDay.type === 'hiit' ? '过渡日'
        : memberDay.type === 'active-rest' ? '主动恢复' : '休息日'
    : scheduleCarbDay === 'high' ? '训练日' : scheduleCarbDay === 'medium' ? '过渡日' : '休息日';
  const targets = plan ? calcPlanDailyTargets({
    template: plan.planTemplate || 'generic', weight: plan.weight, gender: plan.gender,
    age: plan.age ?? profile.age, activityFactor: plan.activityFactor,
    height: plan.height, bodyType: plan.bodyType, weeklyHours: weeklyHoursAtDate(plan, date),
    goal: plan.goal, carbDay: scheduleCarbDay, dayIndex,
  }) : null;
  const stableMacros = isAttachedFatLoss(plan?.planTemplate || 'generic');
  return { dayIndex, carbDay, carbDayLabel: stableMacros ? '每日定量' : carbDay === 'high' ? '高碳日' : carbDay === 'medium' ? '中碳日' : '低碳日',
    carbDaySubLabel, isTrainingDay, targets, snapshot: undefined, template: plan?.planTemplate || 'generic' };
}
